<template>
  <div v-if="initialized">
    <section v-if="initializationError" class="octobus__section error">
      <div :class="{ 'octobus__container': !isFullWidth }">
        <div class="octobus__columns content">
          <div class="octobus__column has-text-centered is-size-4">
            {{$t('success.initError')}}<br>
            <small>({{initializationError}})</small>
          </div>
        </div>
      </div>
    </section>
    <section v-else class="octobus__section success">
      <div :class="{ 'octobus__container': !isFullWidth }" v-if="isTicketsData">
        <div class="octobus__columns noprint has-text-centered">
          <div v-if="tickets.reserve" class="octobus__column" v-html="$t('success.accepted')"></div>
          <div v-else class="octobus__column">
            <h2 class="octobus__title is-3">{{$t('success.thankyou', {brand})}}</h2>
            <p class="octobus__subtitle is-4">{{$t('success.sended')}}</p>
          </div>
        </div>
        <div class="octobus__columns noprint has-text-centered">
          <div class="octobus__column">
            <a
              class="octobus__button is-primary"
              @click="print"
            >
              <span class="octobus__icon">
                <font-awesome-icon :icon="printIcon"></font-awesome-icon>
              </span>
              <span>{{$t('success.print')}}</span>
            </a>
            <a
              class="octobus__button is-primary"
              :class="{'is-loading': loading.link}"
              @click="downloadTicket"
            >
              <span class="octobus__icon">
                <font-awesome-icon :icon="downoladIcon"></font-awesome-icon>
              </span>
              <span>{{$t('success.download')}}</span>
            </a>
          </div>
        </div>
        <template v-if="tickets.bl && tickets.bl.length">
          <Boarding
            v-for="ticket in tickets.bl"
            :ticket="ticket"
            :key="ticket.ticketNum"
            :createdAt="tickets.createdAt"
            :bookingExpiresAt="tickets.bookingExpiresAt"
            :orderNum="tickets.orderNum"
            :sum="tickets.sum"
            :reserve="tickets.reserve"
            :curr="tickets.curr"
            :customType="tickets.customType"
            :eTicket="tickets.eTicket"
            :locale="locale"
          >
          </Boarding>
        </template>
      </div>
      <div :class="{ 'octobus__container': !isFullWidth }" v-else>
        <div class="octobus__columns has-text-centered">
          <div class="octobus__column">
            <p class="octobus__subtitle is-4">{{$t('success.thankyou2')}}</p>
              <router-link :to="{ 'name': 'Main' }" class="has-text-primary">
                {{$t('success.sended2')}}
              </router-link>
          </div>
        </div>
      </div>
      <ErrorModal v-if="downloadError" :show="true" :error="downloadError" @hideModal="onHideDownloadError" />
    </section>
  </div>
</template>

<script>
import api from '@/api';
import { getFileName, errorFromBlob, getOrderData, setOrderData, clearOrderFlowData, isUrlLang } from '@/utils/utils';
import FileSaver from 'file-saver';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import { faDownload, faPrint } from '@fortawesome/fontawesome-free-solid';
import ErrorModal from '@/components/_common/ErrorModal';
import Boarding from './BoardingTicket/Boarding';

export default {
  name: 'Success',
  components: {
    Boarding,
    ErrorModal,
    FontAwesomeIcon,
  },
  beforeRouteEnter: (to, from, next) => {
    const order = getOrderData() || {};
    const { buyid, reserve } = order;
    const isOrderPaid = orderData => (orderData.bl || []).some(t => !!t.paidAt);

    if (!buyid) { // there is no order ID
      next(vm => vm.setInitialized());
      return;
    }

    // it was booking or we already have paid order data
    if (reserve || isOrderPaid(order)) {
      next(vm => vm.setInitialized());
      return;
    }

    api.Buy.getOrder(buyid)
      .then((response) => {
        const { error } = response;
        if (isOrderPaid(response)) {
          const updatedOrder = response;
          updatedOrder.buyid = buyid;
          updatedOrder.reserve = reserve;
          setOrderData(updatedOrder);
          next(vm => vm.setInitialized());
        } else if (error && (error.code !== 'E_NOPRINTFORM')) {
          // failed to get order from the backend and it has not been cancelled
          next(vm => vm.setInitializationError(error));
        } else {
          next({ name: 'Error', replace: true, params: isUrlLang ? { locale: to.params.locale } : {} });
        }
      })
      .catch((error) => {
        // 5xx HTTP errors
        next(vm => vm.setInitializationError(error));
      });
  },
  data() {
    return {
      urlLang: this.$UrlLang,
      locale: this.$framelocale,
      tickets: {},
      downloadLink: '',
      loading: {
        link: false,
      },
      dataFile: '',
      downloadError: null,
      initialized: false,
      initializationError: null,
    };
  },
  props: {
  },
  methods: {
    setInitialized() {
      this.initialized = true;
    },
    setInitializationError(error) {
      this.initializationError = this.getErrorText(error);
      this.setInitialized();
    },
    getErrorText(error) {
      if (error instanceof Object && error.name) {
        return error.name + (error.param ? ` (${error.param})` : '');
      }
      if (error instanceof Object && error.status) {
        return `${error.status} ${error.statusText || ''}`;
      }
      return (typeof error === 'string' || error instanceof String ? error : null) || 'error';
    },
    downloadTicket() {
      if (this.tickets.buyid) {
        this.loading.link = true;

        api.Content.exportPDF({ buyId: this.tickets.buyid, justBought: true }).then((response) => {
          this.loading.link = false;
          if (!response.ok) {
            return errorFromBlob(response.body).then(err => Promise.reject(err));
          }

          FileSaver.saveAs(response.body, getFileName(response));
          clearOrderFlowData();
          return true;
        }, () => {
          this.loading.link = false;
        }).catch((error) => {
          this.downloadError = (typeof error === 'string' || error instanceof String ? error : null) || this.$t('success.downloadError');
        });
      }
    },
    onHideDownloadError() {
      this.downloadError = null;
    },
    print() {
      window.print();
      clearOrderFlowData();
    },
  },
  computed: {
    downoladIcon() {
      return faDownload;
    },
    printIcon() {
      return faPrint;
    },
    isTicketsData() {
      return Boolean(this.tickets.buyid);
    },
    brand() {
      return this.$frameparams.brand;
    },
    isFullWidth() {
      return this.$fullwidth;
    },
  },
  filters: {
  },
  created() {
    this.tickets = getOrderData() || {};
  },
};
</script>

<style lang="scss">
@media print {
  .noprint {
    display: none !important
  }
  .wrapper>div {
    overflow: visible !important
  }
  .prepere-for-print {
    display: block !important
  }
  .boarding-ticket .ticket-page-break {
    page-break-after: always
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Success.vue